import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { FieldError } from "../ui/field";
import { cn } from "../../lib/utils";
import { MAX_ROOM_PARTICIPANTS } from "../../../shared/room-limits.ts";

// Shared look for the home dialogs and the room entry form.
export const roomDialogClass =
  "max-h-[calc(100dvh-2rem)] w-[min(28.75rem,calc(100vw-2rem))] overflow-y-auto rounded-2xl border-0 p-7.5";
export const roomDialogTitleClass = "text-3xl leading-tight font-normal tracking-tighter";
export const roomDialogDescriptionClass = "mt-1 leading-loose text-muted-foreground";
export const roomFormClass = "mt-2.5 flex flex-col gap-6";
export const roomInputClass = "block read-only:text-muted-foreground aria-invalid:ring-0";
export const roomErrorClass = "text-destructive";
export const roomFormNoteClass = "mt-1.75 text-xs leading-relaxed text-muted-foreground";
// eslint-disable-next-line react-refresh/only-export-components -- Shared form styling intentionally accompanies the field component; edits may reload importers.
export const roomFormErrorClass = cn(roomFormNoteClass, roomErrorClass);
export const roomLabelClass = "mb-2";

// Validates the API input built from the form and reports each schema error
// code on the field that produced it.
// eslint-disable-next-line react-refresh/only-export-components -- Shared form validation intentionally accompanies the field component; edits may reload importers.
export function roomFieldErrors(schema: z.ZodType, input: unknown) {
  const result = schema.safeParse(input);
  if (result.success) return undefined;
  const fields: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0] === "maxParticipants" ? "limit" : issue.path[0];
    fields[String(key ?? "name")] ??= issue.message;
  }
  return { fields };
}

// eslint-disable-next-line react-refresh/only-export-components -- Shared form error formatting intentionally accompanies the field component; edits may reload importers.
export function roomErrorMessage(t: ReturnType<typeof useTranslation>["t"], code: string) {
  return t(`home.errors.${code}`, {
    max: MAX_ROOM_PARTICIPANTS,
    defaultValue: t("home.errors.default"),
  });
}

export function RoomFieldError({ errors }: { errors: unknown[] }) {
  const { t } = useTranslation();
  const code = errors.find((error) => typeof error === "string");
  return code ? (
    <FieldError className={roomErrorClass}>{roomErrorMessage(t, code)}</FieldError>
  ) : null;
}
